import React, { useState, useEffect } from "react";
import {
  FileText,
  Users,
  Upload,
  ArrowLeft,
  Download,
  CheckCircle,
  XCircle,
  Search,
  Folder,
  Loader2,
  Activity,
  BarChart3,
  X,
} from "lucide-react";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Importing our services
import { 
  getAllEmployeesList, 
  getEmployeeDocuments, 
  uploadDocForEmployee, 
  updateDocumentStatus,
  adminDownloadDoc 
} from "../services/employee.service";

// --- ACTIVITY LOGS BUTTON COMPONENT ---
const ActivityLogsButton = () => {
  const [showModal, setShowModal] = useState(false);

  const activityLogs = [
    { id: 1, employeeId: 'EMP001', action: 'Uploaded Document', modifiedBy: 'John Doe', date: '2024-01-15', details: 'Uploaded Personal ID Card to Personal Documents' },
    { id: 2, employeeId: 'EMP002', action: 'Document Approved', modifiedBy: 'HR Manager', date: '2024-01-14', details: 'Approved Education Certificate for Jane Smith' },
    { id: 3, employeeId: 'EMP003', action: 'Document Rejected', modifiedBy: 'Admin User', date: '2024-01-13', details: 'Rejected Experience Letter - Invalid format' },
    { id: 4, employeeId: 'EMP001', action: 'Document Downloaded', modifiedBy: 'HR Admin', date: '2024-01-12', details: 'Downloaded Payslip for verification' },
  ];

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center px-3 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium font-roboto"
      >
        <Activity size={16} className="mr-2" />
        Activity Logs
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800 font-poppins">Activity Logs</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 max-h-96 overflow-y-auto">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase font-roboto">Employee ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase font-roboto">Action</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase font-roboto">Modified By</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase font-roboto">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase font-roboto">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {activityLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 font-roboto">{log.employeeId}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-roboto">{log.action}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-roboto">{log.modifiedBy}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-roboto">{log.date}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-roboto">{log.details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// --- REPORTS BUTTON COMPONENT ---
const ReportsButton = () => {
  const [showModal, setShowModal] = useState(false);
  const [reportType, setReportType] = useState('daily');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const activityLogs = [
    { id: 1, employeeId: 'EMP001', action: 'Uploaded Document', modifiedBy: 'John Doe', date: '2024-01-15', details: 'Uploaded Personal ID Card to Personal Documents' },
    { id: 2, employeeId: 'EMP002', action: 'Document Approved', modifiedBy: 'HR Manager', date: '2024-01-14', details: 'Approved Education Certificate for Jane Smith' },
    { id: 3, employeeId: 'EMP003', action: 'Document Rejected', modifiedBy: 'Admin User', date: '2024-01-13', details: 'Rejected Experience Letter - Invalid format' },
    { id: 4, employeeId: 'EMP001', action: 'Document Downloaded', modifiedBy: 'HR Admin', date: '2024-01-12', details: 'Downloaded Payslip for verification' },
  ];

  const downloadExcel = () => {
    const excelData = activityLogs.map(log => ({
      'Employee ID': log.employeeId,
      'Action': log.action,
      'Modified By': log.modifiedBy,
      'Date': log.date,
      'Details': log.details
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Document Activities');
    XLSX.writeFile(wb, `documents-vault-report-${reportType}-${new Date().toISOString().split('T')[0]}.xlsx`);
    setShowModal(false);
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Documents Vault Report - ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`, 20, 20);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
    
    const tableData = activityLogs.map(log => [
      log.employeeId,
      log.action,
      log.modifiedBy,
      log.date,
      log.details
    ]);
    
    doc.autoTable({
      head: [['Employee ID', 'Action', 'Modified By', 'Date', 'Details']],
      body: tableData,
      startY: 40
    });
    
    doc.save(`documents-vault-report-${reportType}-${new Date().toISOString().split('T')[0]}.pdf`);
    setShowModal(false);
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center px-3 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium font-roboto"
      >
        <BarChart3 size={16} className="mr-2" />
        Reports
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800 font-poppins">Generate Reports</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-roboto">Report Type</label>
                    <select
                      value={reportType}
                      onChange={(e) => setReportType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 font-roboto"
                    >
                      <option value="daily">Daily</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="half-yearly">Half Yearly</option>
                      <option value="yearly">Yearly</option>
                      <option value="custom">Custom Date Range</option>
                    </select>
                  </div>
                  
                  {reportType === 'custom' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 font-roboto">Start Date</label>
                        <input
                          type="date"
                          value={dateRange.start}
                          onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 font-roboto"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 font-roboto">End Date</label>
                        <input
                          type="date"
                          value={dateRange.end}
                          onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 font-roboto"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={downloadExcel}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-roboto"
                    >
                      <Download size={16} className="mr-2" />
                      Download Excel
                    </button>
                    <button
                      onClick={downloadPDF}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-roboto"
                    >
                      <Download size={16} className="mr-2" />
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default function DocumentsVault() {
  const [view, setView] = useState("home");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeeDocs, setEmployeeDocs] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rejectionModal, setRejectionModal] = useState({ show: false, docId: null, docName: '' });
  const [rejectionReason, setRejectionReason] = useState('');

  // Standardized folder names matching backend
  const FOLDERS = ["Personal Documents", "Company Letters", "Education Certificates", "Experience Letters", "Id Proofs"];

  // 1. Fetch all employees when "Employees" view is opened
  useEffect(() => {
    if (view === "employees") {
      fetchEmployees();
    }
  }, [view]);

  // 2. Fetch docs when an employee is selected
  useEffect(() => {
    if (selectedEmployee) {
      fetchDocs(selectedEmployee.emp_code);
    }
  }, [selectedEmployee]);

  const fetchEmployees = async () => {
  setLoading(true);
  try {
    const response = await getAllEmployeesList();
    
    // Safety check: handle if backend sends { data: [...] } or just [...]
    const employeeData = response.data || response; 
    
    if (Array.isArray(employeeData)) {
      setEmployees(employeeData);
    } else {
      console.error("Backend did not return an array. Check /docs");
      setEmployees([]); // Prevent .map() crash
    }
  } catch (err) {
    console.error("Failed to load employees", err);
  } finally {
    setLoading(false);
  }
};
  const fetchDocs = async (empCode) => {
    setLoading(true);
    try {
      const data = await getEmployeeDocuments(empCode);
      setEmployeeDocs(data || []);
    } catch (err) {
      console.error("Failed to load docs", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (docId, status, reason = '') => {
    try {
      await updateDocumentStatus(selectedEmployee.emp_code, docId, status, reason);
      setEmployeeDocs((prev) =>
        prev.map((doc) => (doc.id === docId ? { ...doc, status, rejection_reason: reason } : doc))
      );
      setRejectionModal({ show: false, docId: null, docName: '' });
      setRejectionReason('');
    } catch (err) {
      alert("Error updating status: " + err);
    }
  };

  const openRejectModal = (doc) => {
    setRejectionModal({ show: true, docId: doc.id, docName: doc.file_name });
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    handleStatusChange(rejectionModal.docId, 'REJECTED', rejectionReason);
  };

  const handleUpload = async (e) => {
    if (!e.target.files?.[0] || !activeFolder) return;
    const file = e.target.files[0];
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", activeFolder);

    setUploading(true);
    try {
      await uploadDocForEmployee(selectedEmployee.emp_code, formData);
      fetchDocs(selectedEmployee.emp_code); // Refresh list
    } catch (err) {
      alert("Upload failed: " + err);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await adminDownloadDoc(selectedEmployee.emp_code, doc.id);
      window.open(res.download_url, "_blank");
    } catch (err) {
      alert("Download failed");
    }
  };

  // Filter logic
  const filteredEmployees = employees.filter((e) => 
    e.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    e.emp_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-10 font-roboto">
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@400;500&display=swap');
        .font-poppins { font-family: 'Poppins', sans-serif; }
        .font-roboto { font-family: 'Roboto', sans-serif; }
      `}} />

      <div className="max-w-6xl mx-auto">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-6">
            {view !== "home" && (
              <button
                onClick={() => {
                  setView("home");
                  setActiveFolder(null);
                  setSelectedEmployee(null);
                  setSearchTerm("");
                }}
                className="p-3 rounded-full border bg-white hover:bg-gray-100 transition shadow-sm text-gray-600"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div>
              <h1 className="text-4xl font-bold text-gray-800 font-poppins">Documents Vault</h1>
              <p className="text-gray-500 font-roboto mt-1 uppercase tracking-wider text-xs font-semibold">HR Management Portal</p>
            </div>
          </div>
          <ActivityLogsButton />
          <ReportsButton />
        </div>

        {/* HOME VIEW */}
        {view === "home" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div
              onClick={() => setView("admin")}
              className="bg-white border border-gray-200 rounded-3xl p-10 cursor-pointer flex flex-col items-center gap-4 hover:shadow-xl hover:border-blue-400 transition-all group"
            >
              <div className="p-5 bg-blue-50 text-blue-600 rounded-2xl group-hover:bg-blue-600 group-hover:text-white transition-colors">
                <FileText size={40} />
              </div>
              <h2 className="font-bold text-xl text-gray-700 font-poppins text-center">Admin Documents</h2>
              <p className="text-gray-400 text-center text-sm font-roboto">Manage company-wide folders and global uploads.</p>
            </div>
            <div
              onClick={() => setView("employees")}
              className="bg-white border border-gray-200 rounded-3xl p-10 cursor-pointer flex flex-col items-center gap-4 hover:shadow-xl hover:border-purple-400 transition-all group"
            >
              <div className="p-5 bg-purple-50 text-purple-600 rounded-2xl group-hover:bg-purple-600 group-hover:text-white transition-colors">
                <Users size={40} />
              </div>
              <h2 className="font-bold text-xl text-gray-700 font-poppins text-center">Employee Documents</h2>
              <p className="text-gray-400 text-center text-sm font-roboto">View individual employee records and verification status.</p>
            </div>
          </div>
        )}

        {/* EMPLOYEE LIST */}
        {view === "employees" && (
          <div className="animate-in fade-in duration-300">
            <div className="relative mb-8 max-w-md">
                <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                <input
                    className="w-full border rounded-2xl py-3 pl-12 pr-4 focus:ring-2 focus:ring-purple-400 outline-none transition font-roboto bg-white shadow-sm"
                    placeholder="Search by Name or Employee ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {loading ? (
               <div className="flex justify-center py-20"><Loader2 className="animate-spin text-purple-600" size={40} /></div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredEmployees.map((emp) => (
                    <div
                      key={emp.emp_code}
                      className="bg-white border border-gray-100 p-6 rounded-2xl cursor-pointer hover:shadow-md hover:border-purple-200 transition-all"
                      onClick={() => {
                        setSelectedEmployee(emp);
                        setView("employeeDocs");
                      }}
                    >
                      <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold font-poppins">
                              {emp.name?.charAt(0)}
                          </div>
                          <div>
                              <p className="font-bold text-gray-800 font-poppins">{emp.name}</p>
                              <p className="text-xs text-gray-500 font-roboto font-medium">{emp.emp_code} • {emp.designation}</p>
                          </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* FOLDER SELECTION & DOCUMENT LIST */}
        {(view === "admin" || (view === "employeeDocs" && selectedEmployee)) && (
          <div className="animate-in fade-in duration-300">
            {view === "employeeDocs" && (
                <div className="flex items-center gap-4 mb-6">
                    <button onClick={() => setView("employees")} className="text-purple-600 font-bold font-poppins text-sm hover:underline">
                        ← Back to List
                    </button>
                    <span className="text-gray-300">|</span>
                    <h2 className="text-xl font-bold font-poppins text-gray-700">Records for {selectedEmployee.name}</h2>
                </div>
            )}

            {!activeFolder ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {FOLDERS.map((folder) => {
                  const count = employeeDocs.filter(d => d.category === folder).length;
                  return (
                    <div
                      key={folder}
                      onClick={() => setActiveFolder(folder)}
                      className="bg-white border border-gray-200 rounded-2xl p-6 cursor-pointer hover:border-blue-500 hover:bg-blue-50/30 transition-all group flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <Folder className="text-blue-500 group-hover:fill-blue-500 transition-all" size={24} />
                        <p className="font-bold text-gray-700 font-poppins">{folder}</p>
                      </div>
                      <span className="text-sm font-bold text-gray-500">{count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-8 border-b pb-4">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setActiveFolder(null)} className="text-blue-600 font-bold font-poppins">← All Folders</button>
                        <h3 className="text-2xl font-bold font-poppins text-gray-800">{activeFolder}</h3>
                    </div>
                    
                    <label className={`flex items-center gap-2 cursor-pointer bg-blue-600 text-white px-6 py-2.5 rounded-xl hover:bg-blue-700 transition font-poppins font-semibold shadow-lg active:scale-95 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                        {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />} 
                        {uploading ? 'Uploading...' : 'Upload New'}
                        <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
                    </label>
                </div>

                {loading ? (
                   <div className="flex justify-center py-10"><Loader2 className="animate-spin text-blue-600" size={30} /></div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 font-roboto">
                  {employeeDocs
                    .filter((d) => d.category === activeFolder)
                    .map((doc) => (
                      <div key={doc.id} className="border border-gray-100 bg-gray-50/50 p-5 rounded-2xl group hover:border-blue-200 transition-all">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <h4 className="text-gray-700 font-bold font-poppins text-left transition-colors">
                              {doc.file_name}
                            </h4>
                            <div className={`text-[10px] mt-1 inline-block px-2 py-0.5 rounded-full uppercase font-bold tracking-tighter ${
                                doc.status === 'ACCEPTED' ? 'bg-green-100 text-green-700' : 
                                doc.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                                {doc.status}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button className="p-2 text-gray-400 hover:text-blue-600" title="Download" onClick={() => handleDownload(doc)}>
                                <Download size={20} />
                            </button>
                          </div>
                        </div>

                        {/* Show rejection reason if rejected */}
                        {doc.status === "REJECTED" && doc.rejection_reason && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-xs font-bold text-red-700 mb-1">Rejection Reason:</p>
                            <p className="text-xs text-red-600">{doc.rejection_reason}</p>
                          </div>
                        )}

                        {/* If the document was uploaded by an employee and is pending, show Admin controls */}
                        {doc.status === "PENDING" && (
                          <div className="flex gap-4 mt-4 pt-4 border-t border-gray-200">
                            <button 
                                onClick={() => handleStatusChange(doc.id, "ACCEPTED")}
                                className="flex-1 flex items-center justify-center gap-2 bg-green-50 text-green-600 font-bold font-poppins py-2 rounded-lg hover:bg-green-600 hover:text-white transition"
                            >
                              <CheckCircle size={16} /> Approve
                            </button>
                            <button 
                                onClick={() => openRejectModal(doc)}
                                className="flex-1 flex items-center justify-center gap-2 bg-red-50 text-red-600 font-bold font-poppins py-2 rounded-lg hover:bg-red-600 hover:text-white transition"
                            >
                              <XCircle size={16} /> Reject
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {employeeDocs.filter(d => d.category === activeFolder).length === 0 && (
                      <p className="col-span-full text-center text-gray-400 py-10">No documents found in this folder.</p>
                    )}
                </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rejection Reason Modal */}
      {rejectionModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800 font-poppins">Reject Document</h3>
              <button onClick={() => setRejectionModal({ show: false, docId: null, docName: '' })} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4 font-roboto">Document: <span className="font-bold">{rejectionModal.docName}</span></p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a reason for rejection..."
              className="w-full border border-gray-300 rounded-lg p-3 text-sm font-roboto focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              rows={4}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setRejectionModal({ show: false, docId: null, docName: '' })}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-poppins font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-poppins font-semibold"
              >
                Reject Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}